/**
 * Workspace layout store — binary tree of panels + splits, persisted.
 *
 * Operations:
 *   navigateToView(viewId, params)  — set the focused panel's active view
 *   openInNewSplit(viewId, dir)     — split the focused panel, open view in the new pane
 *   splitPanel(panelId, dir)        — split, duplicating the active view
 *   closePanel(panelId)             — remove panel; collapses split if only sibling remains
 *   maximizePanel(panelId)          — full-screen mode
 *   restoreLayout()                 — exit max
 *   focusPanel(panelId)             — change focus marker
 *
 * Hydrated flag prevents URL ↔ store race during page load.
 */
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import {
  type PanelNode, type ViewInstance, type WorkspaceNode,
  createPanelNode, createSplitNode, createViewInstance,
  isPanelNode, isSplitNode,
} from '@/lib/workspace/types'
import {
  findParent, findPanel, getAllPanels, locateView, nextViewState, replaceNode,
  sameParams, stripTransient,
} from '@/lib/workspace/tree'
import { getViewPath, VIEW_REGISTRY } from '@/lib/workspace/registry'

// Tree helpers live in lib/workspace/tree (pure, testable without a DOM);
// re-exported here because callers have always imported them from the store.
export { findPanel, findViewState, locateView } from '@/lib/workspace/tree'

/** localStorage that swallows write failures (quota, private mode). */
const safeLocalStorage: Storage = {
  get length() { return localStorage.length },
  key: (i) => localStorage.key(i),
  getItem: (k) => localStorage.getItem(k),
  removeItem: (k) => { try { localStorage.removeItem(k) } catch { /* ignore */ } },
  clear: () => { try { localStorage.clear() } catch { /* ignore */ } },
  setItem: (k, v) => {
    try { localStorage.setItem(k, v) } catch {
      // Over quota — most likely a view parked something large in viewState.
      console.warn('[workspace] layout not persisted: localStorage write failed')
    }
  },
}

// ─── Store ──────────────────────────────────────────────────────────

interface WorkspaceState {
  rootNode: WorkspaceNode
  focusedPanelId: string | null
  maximizedPanelId: string | null
  hydrated: boolean

  setHydrated: (v: boolean) => void
  focusPanel: (panelId: string) => void
  navigateToView: (viewId: string, params?: Record<string, string>) => string
  openInNewSplit: (viewId: string, direction: 'horizontal' | 'vertical', params?: Record<string, string>) => string
  addViewToPanel: (panelId: string, viewId: string, params?: Record<string, string>) => string
  setActiveView: (panelId: string, instanceId: string) => void
  closeView: (panelId: string, instanceId: string) => void
  /**
   * Write one key of an instance's `viewState`. `next` is a value or an updater
   * (resolved here, against the live tree, so batched updates don't go stale).
   * `fallback` is the view's initial value, used as `prev` when the key is unset.
   */
  setViewState: (
    instanceId: string,
    key: string,
    next: unknown,
    fallback: unknown,
  ) => void
  /**
   * Publish the instance's address — what a deep link or pop-out of this tab
   * should reopen. Drives the URL when the instance is the focused tab.
   */
  setViewParams: (instanceId: string, params?: Record<string, string>) => void
  splitPanel: (panelId: string, direction: 'horizontal' | 'vertical') => void
  closePanel: (panelId: string) => void
  maximizePanel: (panelId: string) => void
  restoreLayout: () => void
  resetLayout: () => void
  setSplitSizes: (splitId: string, sizes: number[]) => void
}

const initialView = createViewInstance('dashboard')
const initialPanel = createPanelNode([initialView])

/** The view opened in a new pane when the one being split can't be duplicated. */
const FALLBACK_VIEW = 'dashboard'

/**
 * Duplicating a view that isn't instance-safe reproduces the bug this whole
 * layer exists to fix: its state lives in component state or a shared
 * localStorage key, so the two copies fight and whichever one you switch away
 * from comes back blank. Views opt in with `multi` once their state is on the
 * instance.
 */
const canDuplicate = (viewId: string) => VIEW_REGISTRY[viewId]?.multi === true

/** Where a view is already open, searching the focused panel first. */
function findOpenInstance(
  rootNode: WorkspaceNode, viewId: string, preferPanelId: string | null,
): { panel: PanelNode; view: ViewInstance } | null {
  const panels = getAllPanels(rootNode)
  const ordered = preferPanelId
    ? [...panels.filter((p) => p.id === preferPanelId), ...panels.filter((p) => p.id !== preferPanelId)]
    : panels
  for (const panel of ordered) {
    const view = panel.views.find((v) => v.viewId === viewId)
    if (view) return { panel, view }
  }
  return null
}

export const useWorkspaceStore = create<WorkspaceState>()(
  persist(
    (set, get) => ({
      rootNode: initialPanel,
      focusedPanelId: initialPanel.id,
      maximizedPanelId: null,
      hydrated: false,

      setHydrated: (hydrated) => set({ hydrated }),

      focusPanel: (panelId) => set({ focusedPanelId: panelId }),

      navigateToView: (viewId, params) => {
        // Sidebar-nav semantics: switch the focused panel to this view.
        // If a tab for the viewId already exists, activate it.
        // Otherwise REPLACE the currently active tab (do not append).
        // New tabs are only added via the panel-header "+" control.
        const { rootNode, focusedPanelId } = get()
        const target = findPanel(rootNode, focusedPanelId) ?? getAllPanels(rootNode)[0]
        if (!target) return getViewPath(viewId, params)

        const existing = target.views.find((v) => v.viewId === viewId)
        let nextPanel: PanelNode
        if (existing) {
          // Activate the tab we already have, and adopt any params that came
          // with the navigation (a deep link, a Screener hand-off) so the URL
          // and the instance agree. Never mint a replacement — that would
          // discard the tab's viewState, which is what Back used to do.
          const views = params && !sameParams(existing.params, params)
            ? target.views.map((v) => (
                v.instanceId === existing.instanceId ? { ...v, params } : v))
            : target.views
          nextPanel = { ...target, views, activeViewInstanceId: existing.instanceId }
        } else {
          const newView = createViewInstance(viewId, params)
          const activeIdx = target.views.findIndex((v) => v.instanceId === target.activeViewInstanceId)
          const idx = activeIdx === -1 ? 0 : activeIdx
          const views = [...target.views]
          views[idx] = newView
          nextPanel = {
            ...target,
            views,
            activeViewInstanceId: newView.instanceId,
          }
        }
        set({
          rootNode: replaceNode(rootNode, target.id, nextPanel),
          focusedPanelId: nextPanel.id,
        })
        return getViewPath(viewId, params)
      },

      addViewToPanel: (panelId, viewId, params) => {
        // "+" tab semantics: append a new tab to the given panel — but only for
        // views that can survive being open twice. For the rest, go to the one
        // that's already open rather than handing back a tab that will blank.
        const { rootNode } = get()
        const target = findPanel(rootNode, panelId)
        if (!target) return getViewPath(viewId, params)
        if (!canDuplicate(viewId)) {
          const open = findOpenInstance(rootNode, viewId, panelId)
          if (open) {
            set({
              rootNode: replaceNode(rootNode, open.panel.id, {
                ...open.panel, activeViewInstanceId: open.view.instanceId,
              }),
              focusedPanelId: open.panel.id,
              maximizedPanelId: null,
            })
            return getViewPath(viewId, open.view.params)
          }
        }
        const newView = createViewInstance(viewId, params)
        const nextPanel: PanelNode = {
          ...target,
          views: [...target.views, newView],
          activeViewInstanceId: newView.instanceId,
        }
        set({
          rootNode: replaceNode(rootNode, target.id, nextPanel),
          focusedPanelId: nextPanel.id,
        })
        return getViewPath(viewId, params)
      },

      openInNewSplit: (viewId, direction, params) => {
        const { rootNode, focusedPanelId } = get()
        const target = findPanel(rootNode, focusedPanelId) ?? getAllPanels(rootNode)[0]
        if (!target) return getViewPath(viewId, params)
        // Ctrl+click a nav item for a single-instance view: focus the instance
        // rather than splitting the layout to show a second, broken copy.
        if (!canDuplicate(viewId)) {
          const open = findOpenInstance(rootNode, viewId, focusedPanelId)
          if (open) {
            set({
              rootNode: replaceNode(rootNode, open.panel.id, {
                ...open.panel, activeViewInstanceId: open.view.instanceId,
              }),
              focusedPanelId: open.panel.id,
              maximizedPanelId: null,
            })
            return getViewPath(viewId, open.view.params)
          }
        }
        const newView = createViewInstance(viewId, params)
        const newPanel = createPanelNode([newView])
        const split = createSplitNode(direction, [target, newPanel])
        set({
          rootNode: replaceNode(rootNode, target.id, split),
          focusedPanelId: newPanel.id,
        })
        return getViewPath(viewId, params)
      },

      setActiveView: (panelId, instanceId) => {
        const { rootNode } = get()
        const panel = findPanel(rootNode, panelId)
        if (!panel) return
        const exists = panel.views.find((v) => v.instanceId === instanceId)
        if (!exists) return
        set({
          rootNode: replaceNode(rootNode, panelId, { ...panel, activeViewInstanceId: instanceId }),
          focusedPanelId: panelId,
        })
      },

      setViewParams: (instanceId, params) => {
        const { rootNode } = get()
        const found = locateView(rootNode, instanceId)
        if (!found || sameParams(found.view.params, params)) return
        const { panel } = found
        set({
          rootNode: replaceNode(rootNode, panel.id, {
            ...panel,
            views: panel.views.map((v) => (
              v.instanceId === instanceId ? { ...v, params } : v)),
          }),
        })
      },

      setViewState: (instanceId, key, next, fallback) => {
        const { rootNode } = get()
        const found = locateView(rootNode, instanceId)
        if (!found) return
        const { panel, view } = found
        const bag = nextViewState(view.viewState, key, next, fallback)
        if (!bag) return  // no-op write
        const nextView: ViewInstance = { ...view, viewState: bag }
        const nextPanel: PanelNode = {
          ...panel,
          views: panel.views.map((v) => (v.instanceId === instanceId ? nextView : v)),
        }
        set({ rootNode: replaceNode(rootNode, panel.id, nextPanel) })
      },

      closeView: (panelId, instanceId) => {
        const { rootNode } = get()
        const panel = findPanel(rootNode, panelId)
        if (!panel) return
        const remaining = panel.views.filter((v) => v.instanceId !== instanceId)
        if (remaining.length === 0) {
          // No tabs left → close the panel entirely.
          get().closePanel(panelId)
          return
        }
        const wasActive = panel.activeViewInstanceId === instanceId
        const nextActive = wasActive ? remaining[0].instanceId : panel.activeViewInstanceId
        set({
          rootNode: replaceNode(rootNode, panelId, {
            ...panel, views: remaining, activeViewInstanceId: nextActive,
          }),
        })
      },

      splitPanel: (panelId, direction) => {
        const { rootNode } = get()
        const panel = findPanel(rootNode, panelId)
        if (!panel) return
        const active = panel.views.find((v) => v.instanceId === panel.activeViewInstanceId)
        if (!active) return
        // The split controls duplicate the active view. When it can't be
        // duplicated safely, still split — the user asked for two panes — but
        // open the dashboard in the new one instead of a copy that would fight.
        const newView = canDuplicate(active.viewId)
          ? createViewInstance(active.viewId, active.params)
          : createViewInstance(FALLBACK_VIEW)
        const newPanel = createPanelNode([newView])
        const split = createSplitNode(direction, [panel, newPanel])
        set({
          rootNode: replaceNode(rootNode, panel.id, split),
          focusedPanelId: newPanel.id,
        })
      },

      closePanel: (panelId) => {
        const { rootNode } = get()
        if (isPanelNode(rootNode) && rootNode.id === panelId) {
          // The only panel — replace with a fresh dashboard panel.
          const fresh = createPanelNode([createViewInstance('dashboard')])
          set({ rootNode: fresh, focusedPanelId: fresh.id, maximizedPanelId: null })
          return
        }
        const parent = findParent(rootNode, panelId)
        if (!parent || !isSplitNode(parent)) return
        const sibling = parent.children[0].id === panelId ? parent.children[1] : parent.children[0]
        const nextRoot = replaceNode(rootNode, parent.id, sibling)
        const remainingPanels = getAllPanels(nextRoot)
        set({
          rootNode: nextRoot,
          focusedPanelId: remainingPanels[0]?.id ?? null,
          maximizedPanelId: null,
        })
      },

      maximizePanel: (panelId) => set({ maximizedPanelId: panelId, focusedPanelId: panelId }),
      restoreLayout: () => set({ maximizedPanelId: null }),
      resetLayout: () => {
        const fresh = createPanelNode([createViewInstance('dashboard')])
        set({ rootNode: fresh, focusedPanelId: fresh.id, maximizedPanelId: null })
      },

      setSplitSizes: (splitId, sizes) => {
        const { rootNode } = get()
        const update = (n: WorkspaceNode): WorkspaceNode => {
          if (isPanelNode(n)) return n
          if (n.id === splitId) return { ...n, sizes }
          return {
            ...n,
            children: [update(n.children[0]), update(n.children[1])] as [WorkspaceNode, WorkspaceNode],
          }
        }
        set({ rootNode: update(rootNode) })
      },
    }),
    {
      name: 'compass.workspace',
      // Guarded: the tree now carries each view's `viewState`, so a page that
      // parks something bulky there could push us past the localStorage quota.
      // A failed write must cost the layout its persistence, never the session.
      storage: createJSONStorage(() => safeLocalStorage),
      // stripTransient drops session-only viewState (half-built orders, staged
      // rolls) so it survives a tab switch but not a reload.
      partialize: (s) => ({
        rootNode: stripTransient(s.rootNode),
        focusedPanelId: s.focusedPanelId,
      }),
      onRehydrateStorage: () => (state) => { state?.setHydrated(true) },
    },
  ),
)
